import React from 'react';
import { Link } from 'react-router-dom';
import { track } from './analytics';

/**
 * "Test yourself" prompt for the end of study-guide content.
 *
 * Why this exists — the funnel, 14 days to 2026-08-25:
 *
 *   visited          1,023
 *   scrolled           591   (58%)
 *   engaged            420   (71%)
 *   STARTED AN EXAM     33   (8%)     <- the collapse is here
 *   completed           27   (82%)
 *   saw the offer       55
 *   bought               3
 *
 * Of everyone who starts an exam, 82% finish, and the purchase rate from there
 * is healthy. The problem is not the paywall or the price — only 3.2% of
 * visitors ever try the product at all.
 *
 * `/study-guide` carries ~932 users at ~22 seconds each and DID already link to
 * `/mock-exams`. But the link sat below all the content, said "Start Practice
 * Mock Exams" (a 45-minute commitment, unstated), and led to a list page — one
 * more decision before anything happens.
 *
 * So this is not "add a link". It makes the first step small and states its
 * real cost: Quick-Fire takes about a minute and already existed, it just had
 * no route until `?quickfire=1` was added in App.js. The full exam stays
 * available for anyone ready for it, with its true cost on the label.
 *
 * @param {string} topic  Module name, recorded on the event so we can see which
 *                        modules actually send people to practise.
 */
export default function TestYourselfPrompt({ topic = 'study_guide' }) {
    return (
        <div className="my-10 rounded-2xl border border-ink-200 bg-ink-50 p-6 shadow-lift-1">
            <p className="text-lg font-display font-bold text-ink-900 mb-1">
                Does it actually stick?
            </p>
            <p className="text-sm text-ink-600 mb-5 u-measure">
                Reading is the easy part. The quickest way to find out what you
                genuinely know is to answer a few questions.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
                {/* Opens Quick-Fire IN PLACE via QuickFireOpener in App.js —
                    deliberately a button, not a link. An earlier version used
                    `/?quickfire=1`, which navigated to the homepage first, so
                    cancelling the modal stranded the visitor there instead of
                    returning them to this page. Quick-Fire is a full-screen
                    overlay available on every route; no navigation is needed.

                    Roughly a minute, which is the point: someone arriving from
                    Facebook will not commit 45 minutes cold. */}
                <button
                    type="button"
                    onClick={() => {
                        track('test_yourself_click', { topic, choice: 'quick' });
                        window.dispatchEvent(new CustomEvent('lifeinuk:open-quickfire'));
                    }}
                    className="flex-1 inline-flex flex-col items-center justify-center bg-action-600 hover:bg-action-700 text-white font-bold px-5 py-3 min-h-tap rounded-xl transition shadow-lift-2 text-center"
                >
                    <span>Try a few quick questions</span>
                    <span className="text-xs font-medium opacity-90">about a minute</span>
                </button>

                <Link
                    to="/mock-exams"
                    onClick={() => track('test_yourself_click', { topic, choice: 'full' })}
                    className="flex-1 inline-flex flex-col items-center justify-center bg-ink-50 hover:bg-ink-100 text-action-text border border-ink-200 hover:border-action-500 font-bold px-5 py-3 min-h-tap rounded-xl transition text-center"
                >
                    <span>Take a full mock exam</span>
                    <span className="text-xs font-medium text-ink-500">24 questions, 45 min</span>
                </Link>
            </div>

            <p className="text-xs text-ink-500 mt-3">
                Free · No sign-up · 3 full exams included
            </p>
        </div>
    );
}
